import { Routes } from '@angular/router';
import { personalSecretaryRoute } from './personal-secretary/personal-secretary.routes';
import { LoginComponent } from './shared/components/login/login.component';
import { authGuard } from './shared/guards/auth.guard';
import { travelOwnerAuthGuard } from './shared/guards/travel-owner-auth.guard';
import { superAdminEnquiriesGuard } from './shared/guards/super-admin-enquiries.guard';
import { placesParentRoutes } from './megha/places/places.routes';

const loadPublicConsultancyProfile = () =>
  import('./megha/digital-profile-biz/public-profile-consultancy.component').then(
    m => m.PublicProfileConsultancyComponent
  );

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./shared/components/app-home/app-home.component').then(m => m.AppHomeComponent)
  },

  // Login and Registration routes
  { path: 'login', component: LoginComponent },
  {
    path: 'auth/login',
    loadComponent: () =>
      import('./shared/components/auth-login/auth-login.component').then((m) => m.AuthLoginComponent)
  },
  { path: 'register', redirectTo: 'login?mode=register', pathMatch: 'full' },
  
  // Password Reset routes
  { 
    path: 'reset-password', 
    loadComponent: () => import('./shared/components/reset-password-request/reset-password-request.component').then(m => m.ResetPasswordRequestComponent)
  },
  { 
    path: 'reset-password-confirm', 
    loadComponent: () => import('./shared/components/reset-password-confirm/reset-password-confirm.component').then(m => m.ResetPasswordConfirmComponent)
  },
  
  // Account settings — inside My Digital Home shell
  { path: 'profile', redirectTo: 'mydigitalhome/profile', pathMatch: 'full' },

  // My Digital Home — signed-in members without a store/profile (favorites, profiles, websites)
  { path: 'hub', redirectTo: 'mydigitalhome', pathMatch: 'prefix' },
  {
    path: 'mydigitalhome',
    loadChildren: () =>
      import('./megha/member-profile/profile-shell.routes').then(m => m.profileShellRoutes)
  },

  {
    path: 'digitalprofile',
    loadComponent: () =>
      import('./megha/service-showcase/vyasa-showcase.component').then(m => m.VyasaShowcaseComponent)
  },
  {
    path: 'digitalbusiness',
    loadComponent: () =>
      import('./megha/service-showcase/vruksha-showcase.component').then(m => m.VrukshaShowcaseComponent)
  },
  {
    path: 'digitaldesign',
    loadComponent: () =>
      import('./megha/service-showcase/maya-showcase.component').then(m => m.MayaShowcaseComponent)
  },
  {
    path: 'vyasa',
    loadComponent: () =>
      import('./megha/service-showcase/vyasa-showcase.component').then(m => m.VyasaShowcaseComponent)
  },
  {
    path: 'vruksha',
    loadComponent: () =>
      import('./megha/service-showcase/vruksha-showcase.component').then(m => m.VrukshaShowcaseComponent)
  },
  {
    path: 'maya',
    loadComponent: () =>
      import('./megha/service-showcase/maya-showcase.component').then(m => m.MayaShowcaseComponent)
  },

  // Legacy /bookmarks: same landing as `/` (Angular app home)
  { path: 'home', redirectTo: '', pathMatch: 'full' },
  
  // My Business — inside My Digital Home shell
  { path: 'mybusiness', redirectTo: 'mydigitalhome/add-business', pathMatch: 'full' },
  
  // Business Items route - Lazy loaded (accessed from mybusiness) - Protected with auth guard
  { 
    path: 'business-items', 
    loadComponent: () => import('./shared/components/business-items/business-items.component').then(m => m.BusinessItemsComponent),
    canActivate: [authGuard]
  },
  { 
    path: 'business-items/:storeId', 
    loadComponent: () => import('./shared/components/business-items/business-items.component').then(m => m.BusinessItemsComponent),
    canActivate: [authGuard]
  },

  {
    path: 'subscriptions',
    redirectTo: 'mydigitalhome/subscriptions',
    pathMatch: 'full'
  },
  {
    path: 'subscriptions/payment/callback',
    redirectTo: 'mydigitalhome/subscriptions/payment/callback',
    pathMatch: 'full'
  },
  { path: 'mysubscription-admin', redirectTo: 'mydigitalhome/subscriptions', pathMatch: 'full' },

  {
    path: 'admin-mobile-stock/:storeCode',
    loadComponent: () =>
      import('./megha/stock-transactions/admin').then(m => m.MobileStoreStockAdminComponent),
    canActivate: [authGuard]
  },

  {
    path: 'admin-hostel-fees/:storeCode',
    redirectTo: 'mybusiness-hostel/:storeCode/fees',
    pathMatch: 'full'
  },

  {
    path: 'mybusiness-hostel/:storeCode',
    loadChildren: () =>
      import('./megha/hostel-business-dashboard/hostel-business.routes').then(
        (m) => m.hostelBusinessRoutes
      )
  },

  {
    path: 'mybusiness-hotel/:storeCode',
    loadChildren: () =>
      import('./megha/hotel-business-dashboard/hotel-business.routes').then(
        (m) => m.hotelBusinessRoutes
      )
  },

  {
    path: 'mybusiness-clinic/:storeCode',
    loadChildren: () =>
      import('./megha/clinic-business-dashboard/clinic-business.routes').then(
        (m) => m.clinicBusinessRoutes
      )
  },

  {
    path: 'mybusiness-pet/:storeCode',
    loadChildren: () =>
      import('./megha/pet-business-dashboard/pet-business.routes').then(
        (m) => m.petBusinessRoutes
      )
  },

  {
    path: 'mybusiness-consult/:storeCode',
    loadChildren: () =>
      import('./megha/profile-business-dashboard/profile-business.routes').then(
        (m) => m.profileBusinessRoutes
      )
  },

  /** Mobile / mobile-care retail — same business dashboard, not the consult shell. */
  {
    path: 'mybusiness-mobile/:storeCode',
    loadChildren: () =>
      import('./megha/profile-business-dashboard/profile-business.routes').then(
        (m) => m.profileBusinessRoutes
      )
  },

  /** Legacy alias — prefer `/mybusiness-consult/:storeCode`. */
  {
    path: 'mybusiness-profile/:storeCode',
    loadChildren: () =>
      import('./megha/profile-business-dashboard/profile-business.routes').then(
        (m) => m.profileBusinessRoutes
      )
  },

  {
    path: 'mybusiness-food/:storeCode',
    loadChildren: () =>
      import('./megha/food-vruksha/food-business.routes').then((m) => m.foodBusinessRoutes)
  },

  {
    path: 'mybusiness-grocery/:storeCode',
    loadChildren: () =>
      import('./megha/business-grocery/grocery-business.routes').then(
        (m) => m.groceryBusinessRoutes
      )
  },

  {
    path: 'mybusiness-fashion/:storeCode',
    loadChildren: () =>
      import('./megha/business-fashion/fashion-business.routes').then(
        (m) => m.fashionBusinessRoutes
      )
  },

  {
    path: 'mybusiness-travel',
    pathMatch: 'full',
    canActivate: [travelOwnerAuthGuard],
    loadComponent: () => import('./megha/business-travels/travel-business-directory.component').then(m => m.TravelBusinessDirectoryComponent)
  },
  {
    path: 'mybusiness-travel/:storeCode',
    loadChildren: () =>
      import('./megha/business-travels/travel-business.routes').then((m) => m.travelBusinessRoutes)
  },

  {
    path: 'mybusiness-realty/:storeCode',
    loadChildren: () =>
      import('./megha/business-realty/realty-business.routes').then((m) => m.realtyBusinessRoutes)
  },

  {
    path: 'mybusiness-interior/:storeCode',
    loadChildren: () =>
      import('./megha/business-interior/interior-business.routes').then(
        (m) => m.interiorBusinessRoutes
      )
  },

  {
    path: 'mybusiness-school/:storeCode',
    loadChildren: () =>
      import('./school-maya/school-business-dashboard/school-business.routes').then(
        (m) => m.schoolBusinessRoutes
      )
  },

  {
    path: 'mybusiness-capital/:storeCode',
    loadChildren: () =>
      import('./capital-maya/business-dashboard/capital-business.routes').then(
        (m) => m.capitalBusinessRoutes
      )
  },

  { path: 'admin-clinic/addcustomer/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/addcustomer', pathMatch: 'full' },
  { path: 'admin-clinic/appointments/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/appointments', pathMatch: 'full' },
  { path: 'admin-clinic/patient/:storeCode/:patientId', redirectTo: 'mybusiness-clinic/:storeCode/patient/:patientId', pathMatch: 'full' },
  { path: 'admin-clinic/products/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/products', pathMatch: 'full' },
  { path: 'admin-clinic/services/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/services', pathMatch: 'full' },
  { path: 'admin-clinic/team/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/team', pathMatch: 'full' },
  { path: 'admin-clinic/attendance/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/attendance', pathMatch: 'full' },
  { path: 'admin-clinic/reports/:storeCode', redirectTo: 'mybusiness-clinic/:storeCode/reports', pathMatch: 'full' },

  {
    path: ':storeCode/patient/:patientId',
    loadComponent: () =>
      import('./megha/pet-business-dashboard/store-patient-redirect.component').then(
        (m) => m.StorePatientRedirectComponent
      ),
    canActivate: [authGuard]
  },

  {
    path: 'digital-profile',
    redirectTo: 'mydigitalhome/my-public-profiles',
    pathMatch: 'full'
  },
  {
    path: 'digital-profile/create',
    loadComponent: () =>
      import('./megha/digital-profile/digital-profile-create-redirect.component').then(
        m => m.DigitalProfileCreateRedirectComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'digital-profile/p/:slug',
    loadComponent: () =>
      import('./megha/digital-profile/digital-profile-hub-redirect.component').then(
        m => m.DigitalProfileHubRedirectComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'digital-profile/p/:slug/edit/:section',
    loadComponent: () =>
      import('./megha/digital-profile/digital-profile-edit-redirect.component').then(
        m => m.DigitalProfileEditRedirectComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'digital-profile/edit/:section',
    loadComponent: () =>
      import('./megha/digital-profile/digital-profile-edit-redirect.component').then(
        m => m.DigitalProfileEditRedirectComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'leader/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'teacher/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'dancer/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'artist/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'doctor/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'influencer/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'profile/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'other/:slug',
    redirectTo: 'profile/:slug'
  },
  {
    path: 'ride',
    pathMatch: 'full',
    loadComponent: () =>
      import('./megha/ride-public/public-ride-page.component').then(
        (m) => m.PublicRideDirectoryComponent
      )
  },
  {
    path: 'ride/captain',
    loadComponent: () =>
      import('./megha/ride-public/ride-captain-onboard.component').then(
        (m) => m.RideCaptainOnboardComponent
      )
  },
  {
    path: 'ride/confirm',
    data: { confirmTrip: true },
    loadComponent: () => import('./megha/ride-public/public-ride-directory.component').then(m => m.PublicRideDirectoryComponent)
  },
  {
    path: 'ride/:slug',
    loadComponent: () =>
      import('./megha/ride-public/public-ride-page.component').then((m) => m.PublicRidePageComponent)
  },
  {
    path: 'travel',
    pathMatch: 'full',
    redirectTo: '/ride'
  },
  {
    path: 'travel/:slug',
    redirectTo: '/ride/:slug',
    pathMatch: 'full'
  },
  {
    path: 'hostel/:slug',
    loadComponent: () =>
      import('./megha/hostel-business-dashboard/public-profile-hostel-store.component').then(
        (m) => m.PublicProfileHostelStoreComponent
      )
  },
  // City parents: /gangtok/leeong-urban-homestay, /vizag/..., /hyderabad/..., …
  ...placesParentRoutes,
  {
    path: 'hotel/:slug',
    loadComponent: () =>
      import('./megha/hotel-business-dashboard/public-profile-hotel-store.component').then(
        (m) => m.PublicProfileHotelStoreComponent
      )
  },
  {
    path: 'pet-care/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'food/:slug',
    loadComponent: () =>
      import('./megha/food-vruksha/public-profile-food-store.component').then(
        (m) => m.PublicProfileFoodStoreComponent
      )
  },
  {
    path: 'business/:storeCode',
    loadComponent: () =>
      import('./megha/business-public/business-public-page.component').then(
        (m) => m.BusinessPublicPageComponent
      )
  },
  {
    path: 'salon/:storeCode',
    loadComponent: () =>
      import('./megha/business-public/business-public-page.component').then(
        (m) => m.BusinessPublicPageComponent
      )
  },
  {
    path: 'pet/:storeCode',
    loadComponent: () =>
      import('./megha/business-public/business-public-page.component').then(
        (m) => m.BusinessPublicPageComponent
      )
  },
  {
    path: 'lawyer/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'student/:slug',
    loadComponent: () =>
      import('./megha/digital-profile-biz/public-profile-student.component').then(
        m => m.PublicProfileStudentComponent
      )
  },
  {
    path: 'consultant/:slug',
    loadComponent: loadPublicConsultancyProfile
  },
  {
    path: 'realestate/:slug',
    loadComponent: () =>
      import('./megha/business-realty/public-profile-realty-store.component').then(
        (m) => m.PublicProfileRealtyStoreComponent
      )
  },
  {
    path: 'realty-estimate/:token',
    loadComponent: () =>
      import('./megha/business-realty/public-realty-estimate.component').then(
        (m) => m.PublicRealtyEstimateComponent
      )
  },
  {
    path: 'interior/:slug',
    loadComponent: loadPublicConsultancyProfile
  },

  // Public Maya dental sites (dentalcastle / roma) are NOT part of tekmegha-home.
  // Workspace: maya-design/dental-clinic → cd there, then npm start | npm run build
  // Deploy from maya-design/dental-clinic/dist/dental-clinic/browser/ only.
  // realestate-maya / landscaping-maya / interior-maya live under maya-design/ (standalone).
  // edu-consultancy (GEN Flyers static site) lives under maya-design/edu-consultancy.
  {
    path: 'capital/:storeCode',
    loadChildren: () =>
      import('./capital-public/capital-public.routes').then((m) => m.capitalPublicRoutes),
  },
  {
    path: 'reality-capital-maya',
    loadComponent: () =>
      import('./capital-public/capital-public-redirect.component').then(
        (m) => m.CapitalPublicRedirectComponent
      ),
  },

  // eng-services lives under maya-design/eng-services (standalone).
  personalSecretaryRoute,

  // Generic Vruksha invoices moved to shared owner invoices under consult shell.
  {
    path: ':storeCode/vruksha/invoices',
    redirectTo: '/mybusiness-consult/:storeCode/invoices',
    pathMatch: 'full',
  },
  {
    path: ':storeCode/vruksha/invoice/create',
    redirectTo: '/mybusiness-consult/:storeCode/invoices/new',
  },
  {
    path: ':storeCode/vruksha/invoice/edit/:id',
    redirectTo: '/mybusiness-consult/:storeCode/invoices/:id',
  },
  {
    path: ':storeCode/vruksha/invoice/view/:id',
    redirectTo: '/mybusiness-consult/:storeCode/invoices/:id/view',
  },
  {
    path: ':storeCode/vruksha/invoice/slip/:id',
    redirectTo: '/mybusiness-consult/:storeCode/invoices/:id/slip',
  },
  {
    path: ':storeCode/vruksha/invoice',
    redirectTo: '/mybusiness-consult/:storeCode/invoices',
    pathMatch: 'full',
  },
  
  // Karna routes - Digital Business Cards: {{storeCode}}/karna
  {
    path: ':storeCode/karna',
    loadComponent: () => import('./karna/digital-card/digital-card.component').then(m => m.DigitalCardComponent)
  },
  
  // Vahi routes - Hackathon Platform
  {
    path: 'vahi',
    loadComponent: () => import('./vahi/vahi-layout/vahi-layout.component').then(m => m.VahiLayoutComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./vahi/vahi-home/vahi-home.component').then(m => m.VahiHomeComponent)
      },
      {
        path: 'login',
        loadComponent: () => import('./vahi/vahi-login/vahi-login.component').then(m => m.VahiLoginComponent)
      },
      {
        path: 'challenge/:id',
        loadComponent: () => import('./vahi/vahi-challenge-detail/vahi-challenge-detail.component').then(m => m.VahiChallengeDetailComponent)
      },
      {
        path: '7day/:id',
        loadComponent: () => import('./vahi/vahi-challenge-7day/vahi-challenge-7day.component').then(m => m.VahiChallenge7dayComponent)
      },
      {
        path: 'exam/:id',
        loadComponent: () => import('./vahi/vahi-online-exam/vahi-online-exam.component').then(m => m.VahiOnlineExamComponent)
      },
      {
        path: 'leaderboard',
        loadComponent: () => import('./vahi/vahi-leaderboard/vahi-leaderboard.component').then(m => m.VahiLeaderboardComponent)
      },
      {
        path: 'profile',
        loadComponent: () => import('./vahi/vahi-profile/vahi-profile.component').then(m => m.VahiProfileComponent)
      },
      {
        path: 'profile/edit',
        loadComponent: () => import('./vahi/vahi-profile-edit/vahi-profile-edit.component').then(m => m.VahiProfileEditComponent)
      },
      {
        path: 'challenge/:id/submit',
        loadComponent: () => import('./vahi/vahi-submission-enhanced/vahi-submission-enhanced.component').then(m => m.VahiSubmissionEnhancedComponent)
      },
      {
        path: 'challenge/:challengeId/team/create',
        loadComponent: () => import('./vahi/vahi-team-create/vahi-team-create.component').then(m => m.VahiTeamCreateComponent)
      },
      {
        path: 'team/:id',
        loadComponent: () => import('./vahi/vahi-team-view/vahi-team-view.component').then(m => m.VahiTeamViewComponent)
      },
      {
        path: 'notifications',
        loadComponent: () => import('./vahi/vahi-notifications/vahi-notifications.component').then(m => m.VahiNotificationsComponent)
      },
      {
        path: 'admin',
        loadComponent: () => import('./vahi/vahi-admin/vahi-admin-dashboard/vahi-admin-dashboard.component').then(m => m.VahiAdminDashboardComponent)
      },
      {
        path: 'admin/challenges',
        loadComponent: () => import('./vahi/vahi-admin/vahi-admin-challenges/vahi-admin-challenges.component').then(m => m.VahiAdminChallengesComponent)
      },
      {
        path: 'admin/week-challenge/:id',
        loadComponent: () => import('./vahi/vahi-week-challenge/vahi-week-challenge.component').then(m => m.VahiWeekChallengeComponent)
      },
      {
        path: 'admin/submissions',
        loadComponent: () => import('./vahi/vahi-admin/vahi-admin-submissions/vahi-admin-submissions.component').then(m => m.VahiAdminSubmissionsComponent)
      },
      {
        path: 'admin/submissions/:submissionId/evaluate',
        loadComponent: () => import('./vahi/vahi-judge-scoring/vahi-judge-scoring.component').then(m => m.VahiJudgeScoringComponent)
      },
      {
        path: 'admin/users',
        loadComponent: () => import('./vahi/vahi-admin/vahi-admin-users/vahi-admin-users.component').then(m => m.VahiAdminUsersComponent)
      }
    ]
  },

  // Custom route redirects (must be before :storeCode catch-all)
  {
    path: 'royalfood',
    redirectTo: 'royalfoodindustry',
    pathMatch: 'full'
  },

  // Explore stores - all stores by category
  {
    path: 'explore-stores',
    loadComponent: () => import('./shared/components/explore-stores/explore-stores.component').then(m => m.ExploreStoresComponent)
  },

  // Enquiries admin - standalone route (reduces :storeCode chunk)
  {
    path: 'admin-enquiries/:storeCode',
    loadComponent: () => import('./vyasa/admin/enquiries-admin/vyasa-enquiries-admin.component').then(m => m.VyasaEnquiriesAdminComponent),
    canActivate: [authGuard]
  },

  // Cross-store enquiries — superadmin user only (last 7 days)
  {
    path: 'super-admin-enquiries',
    loadComponent: () =>
      import('./megha/super-admin-enquiries/super-admin-enquiries.component').then(
        (m) => m.SuperAdminEnquiriesComponent
      ),
    canActivate: [authGuard, superAdminEnquiriesGuard]
  },

  // Food platform default catalog — superadmin only
  {
    path: 'super-admin-food-catalog',
    loadComponent: () =>
      import('./megha/super-admin-food-catalog/super-admin-food-catalog.component').then(
        (m) => m.SuperAdminFoodCatalogComponent
      ),
    canActivate: [authGuard, superAdminEnquiriesGuard]
  },

  // Ride captain profile approvals — Firebase rideAdmins only
  {
    path: 'super-admin-ride-captains',
    loadComponent: () =>
      import('./megha/super-admin-ride-captains/super-admin-ride-captains.component').then(
        (m) => m.SuperAdminRideCaptainsComponent
      )
  },

  // Customers admin - standalone route
  {
    path: 'admin-customers/:storeCode',
    loadComponent: () => import('./vyasa/admin/customers-admin/vyasa-customers-admin.component').then(m => m.VyasaCustomersAdminComponent),
    canActivate: [authGuard]
  },

  // B2B client businesses admin (legacy URL → profile business shell)
  {
    path: 'admin-business-clients/:storeCode',
    redirectTo: 'mybusiness-consult/:storeCode/clients',
    pathMatch: 'full'
  },

  // Book appointment admin - standalone route
  {
    path: 'admin-booknow/:storeCode',
    loadComponent: () => import('./vyasa/admin/booknow-admin/vyasa-booknow-admin.component').then(m => m.VyasaBooknowAdminComponent),
    canActivate: [authGuard]
  },

  // Appointments list admin - standalone route
  {
    path: 'admin-appointments/:storeCode',
    loadComponent: () => import('./vyasa/admin/appointments-admin/vyasa-appointments-admin.component').then(m => m.VyasaAppointmentsAdminComponent),
    canActivate: [authGuard]
  },

  // Blogs admin — clinic stores also use /mybusiness-clinic/:storeCode/blogs
  {
    path: 'admin-blogs/:storeCode',
    loadComponent: () => import('./vyasa/admin/blogs-admin').then(m => m.VyasaBlogsAdminComponent),
    canActivate: [authGuard]
  },

  // Store data items admin - owner-managed items with flexible fields per store_type
  {
    path: 'admin-store-data/:storeCode',
    loadComponent: () => import('./vyasa/admin/store-data-admin').then(m => m.VyasaStoreDataAdminComponent),
    canActivate: [authGuard]
  },

  // Store theme admin - owner-managed theme overrides (colors, logo)
  {
    path: 'admin-store-theme/:storeCode',
    loadComponent: () => import('./vyasa/admin/store-theme-admin').then(m => m.StoreThemeAdminComponent),
    canActivate: [authGuard]
  },

  // Megha profile-linked services (pet-care, travel, etc.) — opened from My Business with ?storeCode=
  {
    path: 'admin-profile-timeline',
    loadComponent: () =>
      import('./megha/digital-profile-biz/megha-profile-timeline-admin.component').then(
        m => m.MeghaProfileTimelineAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'admin-profile-services',
    loadComponent: () =>
      import('./megha/digital-profile-biz/megha-profile-services-admin.component').then(
        m => m.MeghaProfileServicesAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: 'digital-profile-services',
    loadComponent: () =>
      import('./megha/digital-profile-biz/megha-profile-services-admin.component').then(
        m => m.MeghaProfileServicesAdminComponent
      ),
    canActivate: [authGuard]
  },

  // Legacy store URL → public profile (replaces Vyasa ecommerce storefront)
  {
    path: ':storeCode',
    pathMatch: 'full',
    loadComponent: () =>
      import('./vyasa/store-public-redirect/store-public-redirect.component').then(
        m => m.StorePublicRedirectComponent
      )
  },

  // Cart checkout payment flow (kept from Vyasa ecommerce)
  {
    path: ':storeCode/payment/:orderId',
    loadComponent: () =>
      import('./vyasa/vyasa-ecommerce-payment/vyasa-ecommerce-payment.component').then(
        m => m.VyasaEcommercePaymentComponent
      )
  },
  {
    path: ':storeCode/payment/success/:orderId',
    loadComponent: () =>
      import('./vyasa/vyasa-ecommerce-payment-success/vyasa-ecommerce-payment-success.component').then(
        m => m.VyasaEcommercePaymentSuccessComponent
      )
  },
  {
    path: ':storeCode/payment/failure/:orderId',
    loadComponent: () =>
      import('./vyasa/vyasa-ecommerce-payment-failure/vyasa-ecommerce-payment-failure.component').then(
        m => m.VyasaEcommercePaymentFailureComponent
      )
  },
  {
    path: ':storeCode/payment/callback/:orderId',
    loadComponent: () =>
      import('./vyasa/vyasa-ecommerce-payment-callback/vyasa-ecommerce-payment-callback.component').then(
        m => m.VyasaEcommercePaymentCallbackComponent
      )
  },
  {
    path: ':storeCode/payment/callback',
    loadComponent: () =>
      import('./vyasa/vyasa-ecommerce-payment-callback/vyasa-ecommerce-payment-callback.component').then(
        m => m.VyasaEcommercePaymentCallbackComponent
      )
  },

  // Store admin (owner tools — no public storefront layout)
  {
    path: ':storeCode/admin/orders',
    loadComponent: () =>
      import('./vyasa/admin/orders-admin/vyasa-ecommerce-order-management.component').then(
        m => m.VyasaEcommerceOrderManagementComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/categories',
    loadComponent: () =>
      import('./vyasa/admin/categories-admin/vyasa-ecommerce-categories-admin.component').then(
        m => m.VyasaEcommerceCategoriesAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/sliders',
    loadComponent: () =>
      import('./vyasa/admin/slider-admin/vyasa-ecommerce-slider-admin.component').then(
        m => m.VyasaEcommerceSliderAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/deals',
    loadComponent: () =>
      import('./vyasa/admin/deals-admin/vyasa-ecommerce-deals-admin.component').then(
        m => m.VyasaEcommerceDealsAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/about',
    loadComponent: () =>
      import('./vyasa/admin/about-admin/vyasa-ecommerce-about-admin.component').then(
        m => m.VyasaEcommerceAboutAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/blog',
    loadComponent: () =>
      import('./vyasa/admin/blog-admin/vyasa-blog-admin.component').then(m => m.VyasaBlogAdminComponent),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/services',
    loadComponent: () =>
      import('./vyasa/admin/services-admin/vyasa-services-admin-entry.component').then(
        m => m.VyasaServicesAdminEntryComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/standard-services',
    loadComponent: () =>
      import('./vyasa/admin/services-admin/vyasa-services-admin.component').then(
        m => m.VyasaServicesAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/interior-services',
    loadComponent: () =>
      import('./megha/interior-services-admin/interior-services-admin.component').then(
        m => m.InteriorServicesAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/interior-projects',
    loadComponent: () =>
      import('./megha/interior-store-projects-admin/interior-store-projects-admin.component').then(
        m => m.InteriorStoreProjectsAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/team',
    loadComponent: () =>
      import('./vyasa/admin/store-team-admin/vyasa-store-team-admin.component').then(
        m => m.VyasaStoreTeamAdminComponent
      ),
    canActivate: [authGuard]
  },
  {
    path: ':storeCode/admin/featured-projects',
    loadComponent: () =>
      import('./vyasa/admin/featured-projects-admin/vyasa-featured-projects-admin.component').then(
        m => m.VyasaFeaturedProjectsAdminComponent
      ),
    canActivate: [authGuard]
  }
];
